import { useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, ArrowLeftRight, FlaskConical } from "lucide-react";

function DiffList({ title, subtitle, items, color, testid }) {
  const [showTests, setShowTests] = useState(false);
  const real = items.filter((i) => !i.is_test);
  const tests = items.filter((i) => i.is_test);
  return (
    <div className="noam-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-semibold" style={{ color }}>{title}</div>
        <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-zinc-400">
          {real.length} compte{real.length > 1 ? "s" : ""}
        </span>
      </div>
      <div className="text-[11px] text-zinc-500 mb-3">{subtitle}</div>
      {real.length === 0 ? (
        <div className="text-xs text-zinc-500 italic flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#1D9E75]" /> Aucun écart réel.
        </div>
      ) : (
        <div className="space-y-1.5">
          {real.map((u) => (
            <div key={u.email} className="flex items-center justify-between gap-2 bg-black/30 rounded px-3 py-2" data-testid={`env-sync-row-${u.email}`}>
              <div className="min-w-0">
                <div className="text-xs text-zinc-200 truncate">{u.email}</div>
                <div className="text-[10px] text-zinc-500 truncate">{u.name || "—"} · {u.org || "sans org"}</div>
              </div>
              <AlertTriangle className="w-3.5 h-3.5 text-[#EF9F27] shrink-0" />
            </div>
          ))}
        </div>
      )}
      {tests.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowTests((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors"
            data-testid={`${testid}-toggle-tests`}
          >
            <FlaskConical className="w-3 h-3" />
            {showTests ? "Masquer" : "Afficher"} {tests.length} compte{tests.length > 1 ? "s" : ""} de test
          </button>
          {showTests && (
            <div className="space-y-1 mt-2 opacity-60">
              {tests.map((u) => (
                <div key={u.email} className="text-[10px] text-zinc-500 truncate px-3 py-1 bg-black/20 rounded">
                  {u.email} · {u.org || "—"}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EnvSyncPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/ultra-admin/env-sync-check");
      setData(r.data);
    } catch (e) {
      const msg = fmtError(e) || "Comparaison impossible.";
      setError(msg);
      notify.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const realPreviewOnly = (data?.only_preview || []).filter((i) => !i.is_test);
  const realProdOnly = (data?.only_prod || []).filter((i) => !i.is_test);
  const inRealSync = data && realPreviewOnly.length === 0 && realProdOnly.length === 0;

  return (
    <div className="space-y-4" data-testid="env-sync-panel">
      <div className="noam-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-[#378ADD]" />
              Sync preview ↔ production
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 max-w-xl">
              Les deux environnements ont des bases de données séparées. Cet outil compare les comptes
              utilisateurs des deux côtés et signale ceux qui manquent d'un côté ou de l'autre —
              la cause n°1 des « impossible de me connecter en production ».
            </p>
          </div>
          <button
            type="button"
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium bg-[#378ADD]/15 border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/25 transition disabled:opacity-50"
            data-testid="env-sync-run-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {loading ? "Comparaison en cours…" : "Comparer les environnements"}
          </button>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 text-xs text-[#EF6A6A] bg-[#EF6A6A]/10 border border-[#EF6A6A]/30 rounded p-3" data-testid="env-sync-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Comparaison impossible</div>
              <div className="text-zinc-400 mt-0.5">{error}</div>
            </div>
          </div>
        )}

        {data && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="env-sync-stats">
            <div className="bg-black/30 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Preview</div>
              <div className="text-lg font-semibold">{data.preview_total}</div>
            </div>
            <div className="bg-black/30 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Production</div>
              <div className="text-lg font-semibold">{data.prod_total}</div>
            </div>
            <div className="bg-black/30 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Dans les deux</div>
              <div className="text-lg font-semibold text-[#1D9E75]">{data.in_both}</div>
            </div>
            <div className={`rounded p-3 border ${inRealSync ? "bg-[#1D9E75]/10 border-[#1D9E75]/40" : "bg-[#EF9F27]/10 border-[#EF9F27]/40"}`}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Statut</div>
              <div className={`text-sm font-semibold mt-1 ${inRealSync ? "text-[#1D9E75]" : "text-[#EF9F27]"}`} data-testid="env-sync-status">
                {inRealSync ? "Synchronisé ✓" : `${realPreviewOnly.length + realProdOnly.length} écart(s) réel(s)`}
              </div>
            </div>
          </div>
        )}
      </div>

      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <DiffList
            title="Uniquement en PREVIEW"
            subtitle="Ces comptes existent en preview mais PAS en production — ils ne pourront pas se connecter sur noamworks.ch."
            items={data.only_preview || []}
            color="#EF9F27"
            testid="env-sync-only-preview"
          />
          <DiffList
            title="Uniquement en PRODUCTION"
            subtitle="Ces comptes existent en production mais PAS en preview — les tests locaux ne les verront pas."
            items={data.only_prod || []}
            color="#378ADD"
            testid="env-sync-only-prod"
          />
        </div>
      )}
    </div>
  );
}
